export type TimeRangePreset = "1m" | "3m" | "6m" | "ytd" | "1y" | "2y" | "3y" | "5y" | "10y" | "all" | "custom";

export type TimeRangeValue = {
  preset: TimeRangePreset;
  from?: string;
  to?: string;
};

export type ResolvedTimeRange = {
  from: string | null;
  to: string | null;
};

export const timeRangeOptions = [
  { value: "1m", label: "近1月", primary: true },
  { value: "3m", label: "近3月", primary: true },
  { value: "6m", label: "近6月", primary: true },
  { value: "ytd", label: "本年", primary: true },
  { value: "1y", label: "近1年", primary: true },
  { value: "2y", label: "近2年", primary: false },
  { value: "3y", label: "近3年", primary: false },
  { value: "5y", label: "近5年", primary: false },
  { value: "10y", label: "近10年", primary: false },
  { value: "all", label: "全部", primary: true },
  { value: "custom", label: "自定义时间段", primary: false },
] as const satisfies ReadonlyArray<{ value: TimeRangePreset; label: string; primary: boolean }>;

const relativeMonths: Partial<Record<TimeRangePreset, number>> = {
  "1m": 1,
  "3m": 3,
  "6m": 6,
  "1y": 12,
  "2y": 24,
  "3y": 36,
  "5y": 60,
  "10y": 120,
};

function validIsoDate(value: string | null | undefined) {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

function daysInUtcMonth(year: number, monthIndex: number) {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

export function shiftCalendarMonths(dateText: string, months: number) {
  const value = validIsoDate(dateText);
  if (!value) return null;
  const source = new Date(`${value}T00:00:00Z`);
  const absoluteMonth = source.getUTCFullYear() * 12 + source.getUTCMonth() + months;
  const year = Math.floor(absoluteMonth / 12);
  const monthIndex = ((absoluteMonth % 12) + 12) % 12;
  const day = Math.min(source.getUTCDate(), daysInUtcMonth(year, monthIndex));
  return `${String(year).padStart(4, "0")}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function shiftCalendarDays(dateText: string, days: number) {
  const value = validIsoDate(dateText);
  if (!value || !Number.isInteger(days)) return null;
  const source = new Date(`${value}T00:00:00Z`);
  source.setUTCDate(source.getUTCDate() + days);
  return source.toISOString().slice(0, 10);
}

export function presetTimeRange(preset: TimeRangePreset, from?: string, to?: string): TimeRangeValue {
  return preset === "custom" ? { preset, from, to } : { preset };
}

export function timeRangeValueKey(value: TimeRangeValue) {
  return `${value.preset}:${value.from ?? ""}:${value.to ?? ""}`;
}

export function resolveTimeRange(
  value: TimeRangeValue,
  through: string | null | undefined,
  earliest?: string | null,
): ResolvedTimeRange {
  const end = validIsoDate(through);
  const first = validIsoDate(earliest);
  if (value.preset === "custom") {
    const from = validIsoDate(value.from);
    const to = validIsoDate(value.to) ?? end;
    if (from && to && from > to) return { from: to, to: from };
    return { from, to };
  }
  if (!end) return { from: value.preset === "all" ? first : null, to: null };
  if (value.preset === "all") return { from: first, to: end };
  if (value.preset === "ytd") return { from: `${end.slice(0, 4)}-01-01`, to: end };
  const months = relativeMonths[value.preset];
  return { from: months ? shiftCalendarMonths(end, -months) : null, to: end };
}

export function timeRangeLabel(value: TimeRangeValue, through?: string | null, earliest?: string | null) {
  const option = timeRangeOptions.find((item) => item.value === value.preset);
  const resolved = resolveTimeRange(value, through, earliest);
  if (value.preset !== "custom") return option?.label ?? "时间范围";
  if (resolved.from && resolved.to) return `${resolved.from} 至 ${resolved.to}`;
  return option?.label ?? "自定义时间段";
}

export function timeRangeToMonths(value: TimeRangeValue) {
  if (value.preset === "all" || value.preset === "custom" || value.preset === "ytd") return 0;
  return relativeMonths[value.preset] ?? 0;
}

export function timeRangeFromMonths(months: number): TimeRangeValue {
  const preset = Object.entries(relativeMonths).find(([, value]) => value === months)?.[0] as TimeRangePreset | undefined;
  return presetTimeRange(months <= 0 ? "all" : preset ?? "1y");
}

export function parseTimeRangeSearch(params: URLSearchParams, fallback: TimeRangeValue) {
  const preset = params.get("range") as TimeRangePreset | null;
  if (!preset || !timeRangeOptions.some((item) => item.value === preset)) return fallback;
  if (preset !== "custom") return presetTimeRange(preset);
  return presetTimeRange("custom", params.get("from") ?? undefined, params.get("to") ?? undefined);
}

export function writeTimeRangeSearch(params: URLSearchParams, value: TimeRangeValue) {
  params.set("range", value.preset);
  if (value.preset === "custom" && value.from) params.set("from", value.from);
  else params.delete("from");
  if (value.preset === "custom" && value.to) params.set("to", value.to);
  else params.delete("to");
  return params;
}

export function dateLabelBounds(label: string) {
  if (/^\d{4}-Q[1-4]$/.test(label)) {
    const quarter = Number(label.slice(-1));
    const month = (quarter - 1) * 3;
    const start = `${label.slice(0, 4)}-${String(month + 1).padStart(2, "0")}-01`;
    const endDate = new Date(`${start}T00:00:00Z`);
    endDate.setUTCMonth(endDate.getUTCMonth() + 3, 0);
    return { start, end: endDate.toISOString().slice(0, 10) };
  }
  if (/^\d{4}$/.test(label)) return { start: `${label}-01-01`, end: `${label}-12-31` };
  if (/^\d{4}-\d{2}$/.test(label)) {
    const start = `${label}-01`;
    const endDate = new Date(`${start}T00:00:00Z`);
    endDate.setUTCMonth(endDate.getUTCMonth() + 1, 0);
    return { start, end: endDate.toISOString().slice(0, 10) };
  }
  const date = validIsoDate(label.slice(0, 10));
  return date ? { start: date, end: date } : null;
}

export function filterDatedPointsByTimeRange<T>(
  points: T[],
  value: TimeRangeValue,
  label: (point: T) => string,
  through?: string | null,
) {
  const ordered = points.slice().sort((left, right) => label(left).localeCompare(label(right)));
  const first = dateLabelBounds(ordered[0] ? label(ordered[0]) : "")?.start ?? null;
  const last = through ?? dateLabelBounds(ordered.at(-1) ? label(ordered.at(-1)!) : "")?.end ?? null;
  const range = resolveTimeRange(value, last, first);
  return ordered.filter((point) => {
    const bounds = dateLabelBounds(label(point));
    return bounds && (!range.from || bounds.end >= range.from) && (!range.to || bounds.start <= range.to);
  });
}
